import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { usePlanUsage, usePublicPlans, useCheckout } from "@/hooks/use-plan-usage";
import {
  CheckCircle, CreditCard, Calendar, Loader2, AlertTriangle, Shield, Clock,
  ArrowRight, Building2, Plus, XCircle, Zap, TrendingDown, Timer,
  Download, FileText, ExternalLink
} from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";

// Live countdown timer — ticks every second, fires onExpire when reaching zero
function useCountdown(endDate: string | null | undefined): {
  days: number; hours: number; minutes: number; seconds: number; expired: boolean; totalMs: number;
} {
  const calcRemaining = useCallback(() => {
    if (!endDate) return 0;
    return Math.max(0, new Date(endDate).getTime() - Date.now());
  }, [endDate]);

  const [remaining, setRemaining] = useState(calcRemaining);

  useEffect(() => {
    setRemaining(calcRemaining());
    const interval = setInterval(() => {
      const ms = calcRemaining();
      setRemaining(ms);
      if (ms <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [calcRemaining]);

  const totalSeconds = Math.floor(remaining / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: remaining <= 0,
    totalMs: remaining,
  };
}

function CountdownBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-2xl font-bold tabular-nums bg-secondary rounded-md px-3 py-1.5 min-w-[3rem] text-center">
        {String(value).padStart(2, "0")}
      </div>
      <span className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide">{label}</span>
    </div>
  );
}

const OUTCOME_FEATURES = [
  "Handle unlimited transactions automatically",
  "AI categorizes transactions with 95%+ accuracy",
  "Auto-reconciliation with your bank",
  "Instant Profit & Loss, Balance Sheet, and insights",
  "Catch anomalies and errors automatically",
  "Create and send professional invoices",
  "Manage multiple businesses from one dashboard",
  "Priority support when you need it",
];

export default function CustomerSubscription() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { activeBusiness, businesses, deleteBusiness, isPendingDelete } = useBusinesses();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  // "Cancel" chooser — opens when user clicks the destructive button.
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [confirmFullCancel, setConfirmFullCancel] = useState(false);
  const [confirmRemoveBusiness, setConfirmRemoveBusiness] = useState(false);
  const [isCancellingFull, setIsCancellingFull] = useState(false);
  const [showAddLicenseConfirm, setShowAddLicenseConfirm] = useState(false);

  const queryClient = useQueryClient();

  const addLicense = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/add-license");
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.checkoutUrl) {
        toast({ title: "Redirecting to secure checkout..." });
        window.location.href = data.checkoutUrl;
        return;
      }
      if (data.pending) {
        toast({ title: "License purchase processing", description: "Your plan will update momentarily." });
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
          queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
        }, 3000);
        return;
      }
      toast({ title: "License Added", description: data.message || "You can now create a new business." });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
      navigate("/business/create");
    },
    onError: (err: any) => {
      const details = err?.details;
      if (details?.redirectTo) {
        navigate(details.redirectTo);
        return;
      }
      toast({ title: "Failed", description: details?.message || err?.message || "Could not add license", variant: "destructive" });
    },
  });

  const { data: usage, isLoading: usageLoading } = usePlanUsage();
  const { data: licenseInfo } = useQuery({
    queryKey: ["/api/billing/licenses"],
    queryFn: async () => {
      const res = await fetch("/api/billing/licenses", { credentials: "include" });
      return res.json();
    },
  });
  // Fetch real trial status from backend
  const { data: trialStatus } = useQuery({
    queryKey: ["/api/businesses", activeBusiness?.id, "trial-status"],
    queryFn: async () => {
      const res = await fetch(`/api/businesses/${activeBusiness!.id}/trial-status`, { credentials: "include" });
      return res.json();
    },
    enabled: !!activeBusiness?.id,
    refetchInterval: 60000, // refresh every minute
  });
  const { data: plans, isLoading: plansLoading } = usePublicPlans();
  const checkout = useCheckout();

  // Subscription status from billing API — MUST be above any early return (rules of hooks)
  const { data: billingData } = useQuery({
    queryKey: ["/api/billing/subscription", activeBusiness?.id],
    queryFn: async () => {
      const res = await fetch('/api/billing/subscription', { credentials: "include" });
      return res.json();
    },
    enabled: !!activeBusiness?.id,
  });

  // Fetch billing invoices
  const { data: invoices } = useQuery<any[]>({
    queryKey: ["/api/billing/invoices"],
    queryFn: async () => {
      const res = await fetch("/api/billing/invoices", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Derived plan state (safe even when data is loading/null)
  const activePlans = (plans || []).filter((p: any) => p.isActive);
  const currentPlan = activePlans.find((p: any) => p.id === usage?.planId);

  // Failsafe: if billingData shows a stripeSubscriptionId, user is paid — NEVER show as trial
  const hasStripeSubscription = !!(billingData as any)?.stripeSubscriptionId;
  const isTrial = hasStripeSubscription ? false : (!currentPlan || parseFloat(currentPlan?.monthlyPrice || "0") === 0);

  // Countdown hook — MUST be called unconditionally (rules of hooks)
  // Passes null when not on trial → hook returns all zeros safely
  const trialEndDate = trialStatus?.trialEndDate || (usage as any)?.trialEndDate || (usage as any)?.trialEndsAt || null;
  const countdown = useCountdown(isTrial ? trialEndDate : null);

  // Expiry trigger — fire toast when countdown hits zero
  const [hasTriggeredExpiry, setHasTriggeredExpiry] = useState(false);
  useEffect(() => {
    if (isTrial && countdown.expired && trialEndDate && !hasTriggeredExpiry) {
      setHasTriggeredExpiry(true);
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", activeBusiness?.id, "trial-status"] });
      toast({
        title: "Trial Expired",
        description: "Your 14-day free trial has ended. Subscribe to continue using accounting features.",
        variant: "destructive",
      });
    }
  }, [countdown.expired, isTrial, hasTriggeredExpiry, trialEndDate]);

  // Now safe to early-return (all hooks above)
  const isLoading = usageLoading || plansLoading;
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const paidPlan = activePlans.find((p: any) => parseFloat(p.monthlyPrice || "0") > 0);
  const isPaid = !isTrial;
  const trialExpired = trialStatus?.expired ?? countdown.expired;
  const daysRemaining = countdown.days;

  const isCancelling = billingData?.status === "cancelling";
  const isExpired = billingData?.status === "expired" || billingData?.status === "cancelled";
  const isInactive = isCancelling || isExpired;
  const cancelDate = billingData?.currentPeriodEnd
    ? new Date(billingData.currentPeriodEnd).toLocaleDateString()
    : null;

  const handleStartSubscription = () => {
    if (!paidPlan || !activeBusiness) {
      toast({ title: "Error", description: "Unable to start subscription.", variant: "destructive" });
      return;
    }
    setShowUpgradeDialog(true);
  };

  const confirmSubscription = () => {
    if (!paidPlan || !activeBusiness) return;
    checkout.mutate(
      { planId: paidPlan.id, billingCycle: "monthly" },
      { onError: (err: any) => { toast({ title: "Payment failed", description: err.message, variant: "destructive" }); } }
    );
    setShowUpgradeDialog(false);
  };

  return (
    <Layout>
    <div className="container mx-auto p-6 max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">AI Bookkeeping That Works Like a Full-Time Accountant</h1>
        <p className="text-muted-foreground mt-1">
          Categorize, reconcile, and generate reports automatically — for just $25 per business.
        </p>
        <p className="text-xs text-muted-foreground mt-1">No contracts. Cancel anytime.</p>
      </div>

      {/* Live Countdown Timer */}
      {isTrial && !trialExpired && (
        <Card className={daysRemaining <= 3 ? "border-red-500/50 bg-red-50/30" : "border-primary/30"}>
          <CardContent className="py-5">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">
                  {daysRemaining <= 3 ? "Trial expires soon!" : "Free trial time remaining"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <CountdownBox value={countdown.days} label="Days" />
                <span className="text-2xl font-bold text-muted-foreground mt-[-16px]">:</span>
                <CountdownBox value={countdown.hours} label="Hours" />
                <span className="text-2xl font-bold text-muted-foreground mt-[-16px]">:</span>
                <CountdownBox value={countdown.minutes} label="Min" />
                <span className="text-2xl font-bold text-muted-foreground mt-[-16px]">:</span>
                <CountdownBox value={countdown.seconds} label="Sec" />
              </div>
              {daysRemaining <= 3 && (
                <p className="text-xs text-red-600 mt-3">Subscribe now to avoid losing access to accounting features.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expired Alert */}
      {isTrial && trialExpired && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Your free trial has expired</AlertTitle>
          <AlertDescription>
            Accounting features are disabled. Subscribe now to restore full access. Your data is safe.
          </AlertDescription>
        </Alert>
      )}

      {/* Expired Banner */}
      {isExpired && isPaid && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Your subscription has ended</AlertTitle>
          <AlertDescription>
            Your data is safe and you can still view everything. Reactivate to create transactions, invoices, and use all features.
          </AlertDescription>
        </Alert>
      )}

      {/* Cancelling Banner */}
      {isCancelling && !isExpired && isPaid && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertTitle>Your subscription is being cancelled</AlertTitle>
          <AlertDescription>
            {cancelDate
              ? `You'll retain full access until ${cancelDate}. After that, your account will be read-only.`
              : "Your subscription will end at the end of your current billing period."}
          </AlertDescription>
        </Alert>
      )}

      {/* Current Plan Status */}
      <Card className={isPaid ? "border-green-500/50" : "border-yellow-500/50"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {isPaid ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Clock className="h-5 w-5 text-yellow-500" />}
                {isPaid ? "ZentroBooks" : "Free Trial"}
              </CardTitle>
              <CardDescription>
                {isPaid ? "Full access — all features unlocked"
                  : trialExpired ? "Trial expired — accounting features disabled"
                  : `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m remaining — full access`}
              </CardDescription>
            </div>
            <div className="text-right">
              {isPaid ? (
                <>
                  <div className="text-2xl font-bold">$25<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                  {isExpired
                    ? <Badge variant="destructive">Expired</Badge>
                    : isCancelling
                      ? <Badge variant="secondary">Cancelling</Badge>
                      : <Badge variant="default" className="bg-green-600">Active</Badge>
                  }
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">$0</div>
                  <Badge variant={trialExpired ? "destructive" : "secondary"}>
                    {trialExpired ? "Expired" : "Trial"}
                  </Badge>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Upgrade CTA — Trial users and expired subscribers */}
      {(isTrial || isExpired) && (
        <Card className="border-primary bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Zap className="h-5 w-5 text-primary" />
              ZentroBooks — $25/month per business
            </CardTitle>
            <CardDescription className="text-base">
              Everything you need to run your books on autopilot
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {OUTCOME_FEATURES.map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button size="lg" className="w-full text-base" onClick={handleStartSubscription} disabled={checkout.isPending}>
              {checkout.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                : <>Start Subscription <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
            <p className="text-xs text-center text-muted-foreground">No contracts. Cancel anytime. Your data stays safe.</p>
          </CardContent>
        </Card>
      )}

      {/* Why ZentroBooks — Cost Comparison */}
      {(isTrial || isExpired) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-green-600" />
              Why ZentroBooks?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                <p className="text-xs text-red-600 font-medium mb-1">Traditional Bookkeeper</p>
                <p className="text-2xl font-bold text-red-700">$300–$800</p>
                <p className="text-xs text-red-500">/month</p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <p className="text-xs text-green-600 font-medium mb-1">ZentroBooks AI</p>
                <p className="text-2xl font-bold text-green-700">$25</p>
                <p className="text-xs text-green-500">/month per business</p>
              </div>
            </div>
            <p className="text-center text-sm font-semibold text-green-700">
              Save up to 90% on bookkeeping costs
            </p>
          </CardContent>
        </Card>
      )}

      {/* License Usage */}
      {licenseInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Entity Licenses
            </CardTitle>
            <CardDescription>
              {isTrial ? "Trial includes 1 business entity" : `${licenseInfo.licensesTotal} license${licenseInfo.licensesTotal !== 1 ? "s" : ""} purchased`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Entities Used</span>
              <span className="text-lg font-bold">{licenseInfo.licensesUsed} / {licenseInfo.licensesTotal}</span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className={`h-full transition-all ${licenseInfo.licensesUsed >= licenseInfo.licensesTotal ? "bg-red-500" : "bg-green-500"}`}
                style={{ width: `${Math.min((licenseInfo.licensesUsed / Math.max(licenseInfo.licensesTotal, 1)) * 100, 100)}%` }}
              />
            </div>
            {isPaid && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3 gap-1"
                disabled={addLicense.isPending}
                onClick={() => setShowAddLicenseConfirm(true)}
              >
                {addLicense.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                {addLicense.isPending ? "Adding..." : "Add License ($25/mo)"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Billing Details — Paid Users */}
      {isPaid && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Billing Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-2"><CreditCard className="h-4 w-4" />Plan</span>
              <span className="font-medium">ZentroBooks — $25/month</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-2"><Calendar className="h-4 w-4" />Next Billing</span>
              <span className="font-medium">Auto-renews monthly</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-2"><Shield className="h-4 w-4" />Status</span>
              {isCancelling
                ? <Badge variant="destructive">Cancelling{cancelDate ? ` — ends ${cancelDate}` : ""}</Badge>
                : <Badge variant="default" className="bg-green-600">Active</Badge>
              }
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4 flex justify-between items-center">
            <div className="text-xs text-muted-foreground">
              Billing: <span className="font-medium capitalize">{billingData?.billingInterval || "monthly"}</span>
            </div>
            {isCancelling ? (
              <Button
                size="sm"
                disabled={!activeBusiness?.id}
                title={!activeBusiness?.id ? "Select a business in the sidebar first" : undefined}
                onClick={async () => {
                  if (!activeBusiness?.id) {
                    toast({
                      title: "No business selected",
                      description: "Please pick a business in the sidebar — then click again.",
                      variant: "destructive",
                    });
                    return;
                  }
                  try {
                    // Reactivate: set status back to active
                    await fetch(`/api/businesses/${activeBusiness.id}/subscriptions/${billingData?.id}`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ status: "active" }),
                    });
                    // Also update local DB directly
                    await fetch("/api/billing/reactivate", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({}),
                    });
                    toast({ title: "Subscription reactivated", description: "Your subscription is active again." });
                    queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
                  } catch {
                    toast({ title: "Error", description: "Failed to reactivate.", variant: "destructive" });
                  }
                }}
              >
                Reactivate Subscription
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                disabled={!activeBusiness?.id}
                title={!activeBusiness?.id ? "Select a business in the sidebar first" : undefined}
                onClick={() => {
                  if (!activeBusiness?.id) {
                    toast({
                      title: "No business selected",
                      description: "Please pick a business in the sidebar — then click again.",
                      variant: "destructive",
                    });
                    return;
                  }
                  setConfirmFullCancel(false);
                  setConfirmRemoveBusiness(false);
                  setShowCancelDialog(true);
                }}
              >
                Cancel Subscription
              </Button>
            )}
          </CardFooter>
        </Card>
      )}

      {/* Invoices — Paid Users */}
      {isPaid && invoices && invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoices.map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">{inv.invoice_number || 'Invoice'}</div>
                    <div className="text-xs text-muted-foreground">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                      {' · '}
                      ${((inv.amount || 0) / 100).toFixed(2)} {(inv.currency || 'usd').toUpperCase()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="default" className="bg-green-600 text-xs">Paid</Badge>
                    {inv.hosted_url && (
                      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" asChild>
                        <a href={inv.hosted_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      </Button>
                    )}
                    {inv.pdf_url && (
                      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" asChild>
                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-3 w-3" /> PDF
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* What's Included — Paid Users */}
      {isPaid && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">What's Included</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {OUTCOME_FEATURES.map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade Confirmation */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start ZentroBooks Subscription</DialogTitle>
            <DialogDescription>$25/month per business — cancel anytime</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-medium">ZentroBooks</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Price</span><span className="font-medium">$25.00/month</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Billing</span><span className="font-medium">Monthly, cancel anytime</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>Cancel</Button>
            <Button onClick={confirmSubscription} disabled={checkout.isPending}>
              {checkout.isPending ? "Processing..." : "Proceed to Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add License confirmation — explains what happens before redirecting to Stripe */}
      <Dialog open={showAddLicenseConfirm} onOpenChange={setShowAddLicenseConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add another business license?</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                This will <strong>increase your subscription by $25/month</strong> and let you create one more business under your account.
              </span>
              <span className="block text-xs">
                If you already have a paid Stripe subscription, the change is prorated and applied immediately.
                Otherwise, you'll be redirected to a secure Stripe checkout page to complete the purchase.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLicenseConfirm(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setShowAddLicenseConfirm(false);
                addLicense.mutate();
              }}
              disabled={addLicense.isPending}
            >
              {addLicense.isPending ? "Processing..." : "Continue to checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel chooser — distinguishes "drop one business" from "cancel whole sub" */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>What do you want to cancel?</DialogTitle>
            <DialogDescription>
              You have {businesses.length} business{businesses.length === 1 ? "" : "es"}. Pick the action that matches your intent.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Option 1 — drop one business */}
            <div className="rounded-lg border p-4">
              <h3 className="text-sm font-semibold mb-1">Remove this business only</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Deletes <span className="font-medium">{activeBusiness?.name?.trim() || "this business"}</span>.
                Your other {Math.max(0, businesses.length - 1)} business{businesses.length - 1 === 1 ? "" : "es"} keep working,
                and Stripe drops the per-business charge for it on the next bill.
              </p>
              <label className="flex items-start gap-2 text-xs mb-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmRemoveBusiness}
                  onChange={(e) => setConfirmRemoveBusiness(e.target.checked)}
                />
                <span>I understand this permanently deletes <strong>{activeBusiness?.name?.trim() || "this business"}</strong> and its data.</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/50"
                disabled={!confirmRemoveBusiness || !activeBusiness?.id || isPendingDelete}
                onClick={async () => {
                  if (!activeBusiness?.id) return;
                  try {
                    await deleteBusiness(activeBusiness.id);
                    toast({
                      title: "Business removed",
                      description: `"${activeBusiness.name?.trim() || "Business"}" has been deleted. Your subscription will be adjusted on the next bill.`,
                    });
                    setShowCancelDialog(false);
                    queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
                    navigate("/dashboard");
                  } catch (err: any) {
                    toast({ title: "Removal failed", description: err?.message || "Please try again.", variant: "destructive" });
                  }
                }}
              >
                {isPendingDelete ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                Remove this business only
              </Button>
            </div>

            {/* Option 2 — cancel entire sub */}
            <div className="rounded-lg border border-destructive/30 p-4 bg-destructive/5">
              <h3 className="text-sm font-semibold mb-1 text-destructive">Cancel entire subscription</h3>
              <p className="text-xs text-muted-foreground mb-3">
                <strong>All your businesses will lose access at the end of the billing period.</strong>{" "}
                Affects {businesses.length} business{businesses.length === 1 ? "" : "es"}: {businesses.map((b: any) => b.name?.trim() || "Unnamed").join(", ")}.
              </p>
              <label className="flex items-start gap-2 text-xs mb-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmFullCancel}
                  onChange={(e) => setConfirmFullCancel(e.target.checked)}
                />
                <span>I understand <strong>all {businesses.length} business{businesses.length === 1 ? "" : "es"}</strong> will lose access at period end.</span>
              </label>
              <Button
                variant="destructive"
                size="sm"
                disabled={!confirmFullCancel || isCancellingFull}
                onClick={async () => {
                  setIsCancellingFull(true);
                  try {
                    const res = await fetch("/api/billing/cancel", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({}),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.cancelled) {
                      throw new Error(data?.message || "Cancel failed");
                    }
                    const endDate = data.periodEnd
                      || (billingData?.currentPeriodEnd
                          ? new Date(billingData.currentPeriodEnd).toLocaleDateString()
                          : "the end of your billing period");
                    toast({
                      title: "Subscription cancelled",
                      description: `Subscription will end on ${endDate}.`,
                    });
                    setShowCancelDialog(false);
                    queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
                  } catch (err: any) {
                    toast({ title: "Cancel failed", description: err?.message || "Please try again.", variant: "destructive" });
                  } finally {
                    setIsCancellingFull(false);
                  }
                }}
              >
                {isCancellingFull ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                Cancel entire subscription
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCancelDialog(false)}>Keep my subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
